import { settingsT } from '~/composables/hr-evaluation/conf/settings/useHrEvalSettingsI18n';
export type EvalGradeDomain = 'perform' | 'ability';

export interface EvalGradeSettingConflictContext {
  performExecYn: boolean;
  abilityExecYn: boolean;
  performEvalMethodCd: string;
  abilityEvalMethodCd: string;
  performRelativeYn: string;
  perfGradeGrpLevelYn: boolean;
  cmptGradeGrpLevelYn: boolean;
  seniorEvalCd: string;
  seniorEvalMethodCd: string;
}

export interface EvalGradeSettingConflictBase extends EvalGradeSettingConflictContext {
  perfGradeGrpId?: string;
  cmptGradeGrpId?: string;
  secEvalGradeLimitCd?: string;
  thrEvalGradeLimitCd?: string;
}

export type EvalListGradeCard = {
  perfGradeGrpId?: string;
  cmptGradeGrpId?: string;
};

export const buildEvalGradeSettingConflictContext = (
  base: Partial<EvalGradeSettingConflictBase> | null | undefined,
): EvalGradeSettingConflictContext => ({
  performExecYn: !!base?.performExecYn,
  abilityExecYn: !!base?.abilityExecYn,
  performEvalMethodCd: base?.performEvalMethodCd ?? '',
  abilityEvalMethodCd: base?.abilityEvalMethodCd ?? '',
  performRelativeYn: base?.performRelativeYn === 'Y' ? 'Y' : 'N',
  perfGradeGrpLevelYn: !!base?.perfGradeGrpLevelYn,
  cmptGradeGrpLevelYn: !!base?.cmptGradeGrpLevelYn,
  seniorEvalCd: base?.seniorEvalCd ?? '',
  seniorEvalMethodCd: base?.seniorEvalMethodCd ?? '',
});

export const isSecondOrThirdSeniorEval = (seniorEvalCd: string) =>
  seniorEvalCd === 'sec' || seniorEvalCd === 'thr';

export const isSeniorEvalAdjustMode = (ctx: EvalGradeSettingConflictContext) =>
  isSecondOrThirdSeniorEval(ctx.seniorEvalCd) && ctx.seniorEvalMethodCd === '002';

export const isPerformGradeEval = (ctx: EvalGradeSettingConflictContext) =>
  ctx.performExecYn && ctx.performEvalMethodCd === 'grade';

export const isAbilityGradeEval = (ctx: EvalGradeSettingConflictContext) =>
  ctx.abilityExecYn && ctx.abilityEvalMethodCd === 'grade';

/**
 * Step1에서 시행(체크)된 성과/역량 모두에 '평가 종류별 등급'이 켜져 있는지.
 * - 둘 다 시행 + 둘 다 종류별 등급 → true
 * - 둘 다 시행 + 한쪽만 종류별 등급 → false
 * - 한쪽만 시행 + 그쪽 종류별 등급 → true
 */
export const hasAllExecutedDomainsGradeLevelYn = (ctx: EvalGradeSettingConflictContext) => {
  const flags: boolean[] = [];
  if (ctx.performExecYn) flags.push(!!ctx.perfGradeGrpLevelYn);
  if (ctx.abilityExecYn) flags.push(!!ctx.cmptGradeGrpLevelYn);
  return flags.length > 0 && flags.every(Boolean);
};

/** 특정 도메인 종류별 등급을 value로 가정했을 때, 시행 도메인 모두에 종류별 등급이 되는지 */
export const wouldAllExecutedDomainsHaveGradeLevelYn = (
  ctx: EvalGradeSettingConflictContext,
  domain: EvalGradeDomain,
  value: boolean,
) =>
  hasAllExecutedDomainsGradeLevelYn({
    ...ctx,
    perfGradeGrpLevelYn: domain === 'perform' ? value : ctx.perfGradeGrpLevelYn,
    cmptGradeGrpLevelYn: domain === 'ability' ? value : ctx.cmptGradeGrpLevelYn,
  });

/** 002와 충돌: 시행 중인 성과/역량 모두에 종류별 등급이 켜진 경우만 */
export const hasAnyGradeLevelConflict = (ctx: EvalGradeSettingConflictContext) =>
  isSeniorEvalAdjustMode(ctx) && hasAllExecutedDomainsGradeLevelYn(ctx);

/**
 * 002 사용 중일 때 종류별 등급 체크 비활성:
 * 이 도메인을 켜면 시행 도메인 모두가 종류별 등급이 되는 경우
 */
export const isPerfGradeGrpLevelYnDisabled = (
  ctx: EvalGradeSettingConflictContext,
  hasLegacyConflict: boolean,
) =>
  !hasLegacyConflict &&
  isSeniorEvalAdjustMode(ctx) &&
  isPerformGradeEval(ctx) &&
  wouldAllExecutedDomainsHaveGradeLevelYn(ctx, 'perform', true);

export const isCmptGradeGrpLevelYnDisabled = (
  ctx: EvalGradeSettingConflictContext,
  hasLegacyConflict: boolean,
) =>
  !hasLegacyConflict &&
  isSeniorEvalAdjustMode(ctx) &&
  isAbilityGradeEval(ctx) &&
  wouldAllExecutedDomainsHaveGradeLevelYn(ctx, 'ability', true);

export const isPerformRelativeGradeEval = (ctx: EvalGradeSettingConflictContext) =>
  isPerformGradeEval(ctx) && ctx.performRelativeYn === 'Y';

/** 성과 등급 상대평가 시 002 비활성 */
export const isSeniorEvalAdjustDisabledByRelative = (
  ctx: EvalGradeSettingConflictContext,
  hasLegacyRelativeConflict: boolean,
) => !hasLegacyRelativeConflict && isPerformRelativeGradeEval(ctx);

/** 성과 등급 + 002 시 상대평가 비활성 */
export const isPerformRelativeEvalDisabled = (
  ctx: EvalGradeSettingConflictContext,
  hasLegacyRelativeConflict: boolean,
) =>
  !hasLegacyRelativeConflict && isPerformGradeEval(ctx) && ctx.seniorEvalMethodCd === '002';

export const hasPerformRelativeSeniorAdjustConflict = (ctx: EvalGradeSettingConflictContext) =>
  isPerformRelativeGradeEval(ctx) && isSeniorEvalAdjustMode(ctx);

/**
 * 종류별 등급으로 002 비활성:
 * Step1 시행된 성과/역량 모두에 '평가 종류별 등급'이 체크된 경우에만
 */
export const isSeniorEvalAdjustDisabledByGradeLevel = (
  ctx: EvalGradeSettingConflictContext,
  hasLegacyConflict: boolean,
) => !hasLegacyConflict && hasAllExecutedDomainsGradeLevelYn(ctx);

export const isSeniorEvalAdjustDisabled = (
  ctx: EvalGradeSettingConflictContext,
  hasLegacyGradeLevelConflict: boolean,
  hasLegacyRelativeConflict = false,
) =>
  isSeniorEvalAdjustDisabledByGradeLevel(ctx, hasLegacyGradeLevelConflict) ||
  isSeniorEvalAdjustDisabledByRelative(ctx, hasLegacyRelativeConflict);

/** 002 등급조정(±N)에 반영 가능한 등급 평가 영역 존재 여부 */
export const hasGradeEvalDomainForSeniorAdjust = (ctx: EvalGradeSettingConflictContext) =>
  (isPerformGradeEval(ctx) && !ctx.perfGradeGrpLevelYn && ctx.performRelativeYn !== 'Y') ||
  (isAbilityGradeEval(ctx) && !ctx.cmptGradeGrpLevelYn);

export const normalizeEvalListGradeGrpIds = (
  base: Pick<EvalGradeSettingConflictBase, 'perfGradeGrpId' | 'cmptGradeGrpId' | 'perfGradeGrpLevelYn' | 'cmptGradeGrpLevelYn'>,
  evalList: EvalListGradeCard[],
) => {
  if (!Array.isArray(evalList)) return;
  evalList.forEach(card => {
    if (!base.perfGradeGrpLevelYn) {
      card.perfGradeGrpId = base.perfGradeGrpId ?? '';
    }
    if (!base.cmptGradeGrpLevelYn) {
      card.cmptGradeGrpId = base.cmptGradeGrpId ?? '';
    }
  });
};

/** 성과 상대평가 + 002 동시 설정 시 002·등급조정 값 보정 */
export const resetSeniorEvalAdjustForRelativeOnly = (base: EvalGradeSettingConflictBase) => {
  const ctx = buildEvalGradeSettingConflictContext(base);
  if (!hasPerformRelativeSeniorAdjustConflict(ctx)) {
    return false;
  }
  base.seniorEvalMethodCd = '001';
  base.secEvalGradeLimitCd = '';
  base.thrEvalGradeLimitCd = '';
  return true;
};

/** 002 선택 시 상대평가 → 절대평가 보정 */
export const resetPerformRelativeForSeniorAdjustOnly = (base: EvalGradeSettingConflictBase) => {
  const ctx = buildEvalGradeSettingConflictContext(base);
  if (!hasPerformRelativeSeniorAdjustConflict(ctx)) {
    return false;
  }
  base.performRelativeYn = 'N';
  return true;
};

export const ensurePerformRelativeSeniorAdjustCompatibility = (base: EvalGradeSettingConflictBase) => {
  const ctx = buildEvalGradeSettingConflictContext(base);
  if (!hasPerformRelativeSeniorAdjustConflict(ctx)) {
    return false;
  }
  // 레거시 동시 설정: 상대평가 유지, 002 해제
  return resetSeniorEvalAdjustForRelativeOnly(base);
};

/** A: 이전 평가 결과 조정(002) 유지 → 시행 도메인의 종류별 등급 해제 및 카드 척도 통일 */
export const resolveGradeConflictPreferAdjust = (
  base: EvalGradeSettingConflictBase,
  evalList?: EvalListGradeCard[],
) => {
  const ctx = buildEvalGradeSettingConflictContext(base);
  if (ctx.performExecYn) {
    base.perfGradeGrpLevelYn = false;
  }
  if (ctx.abilityExecYn) {
    base.cmptGradeGrpLevelYn = false;
  }
  if (evalList) {
    normalizeEvalListGradeGrpIds(base, evalList);
  }
};

/** B: 평가 종류별 등급 유지 → 상급자 개별 평가(001)로 전환 */
export const resolveGradeConflictPreferLevelYn = (base: EvalGradeSettingConflictBase) => {
  base.seniorEvalMethodCd = '001';
  base.secEvalGradeLimitCd = '';
  base.thrEvalGradeLimitCd = '';
};

export const isGradeGrpLevelYnActive = (value: unknown) => value === true || value === 'Y';
